import { AppHelper } from '../../../../helpers/app.helper';
import { Range } from '../../entities/range.model';
import { Table } from '../../entities/table.model';
import { Cascading } from '../cascading/cascading.model';


export class CascadingTable extends Table<{ [key: string]: Cascading; }> {
  private readonly taxonomyFieldsCache: { [taxonomyId: number]: { fieldId: number; keys: string[]; }[]; } = {};

  constructor(
    private appHelper: AppHelper,
    private permanentColumnsCount: number,
    range: Range) {
    super(range, false, {});
  }

  add(taxonomyId: number, fieldId: number, items: string[]): boolean {
    if (!this.itemsExist(taxonomyId, fieldId, items)) {
      const columnDiff = items.length - (this.range.columnCount - this.permanentColumnsCount);

      if (columnDiff > 0) {
        this.range.columnCount += columnDiff;
      }

      if (this.containsData) {
        this.range.rowCount++;
      } else {
        this.containsData = true;
      }

      const key = this.cacheTaxonomyField(taxonomyId, fieldId);
      this.cache[key] = new Cascading(taxonomyId, fieldId, key, items);

      return true;
    }

    return false;
  }

  itemsExist(taxonomyId: number, fieldId: number, items?: string[]): boolean {
    const cached = this.taxonomyFieldsCache[taxonomyId];
    const cachedField = this.appHelper.find(cached, f => f.fieldId === fieldId);

    if (cachedField) {
      for (const key of cachedField.keys) {
        const cachedItems = this.cache[key].items;

        if (cachedItems.length === items.length && cachedItems.every(i => this.appHelper.includes(items, i))) {
          return true;
        }
      }
    }

    return false;
  }

  keyExists(taxonomyId: number, fieldId: number, key: string): boolean {
    const cached = this.taxonomyFieldsCache[taxonomyId];
    const cachedField = this.appHelper.find(cached, f => f.fieldId === fieldId);

    return cachedField?.keys.indexOf(key) >= 0;
  }

  getFieldKeys(taxonomyId: number, fieldId: number): string[] {
    const cached = this.taxonomyFieldsCache[taxonomyId];
    const keys = this.appHelper.find(cached, f => f.fieldId === fieldId)?.keys;

    return keys ? [...keys] : undefined;
  }

  getRowPosition(taxonomyId: number, fieldId: number, key: string): number {
    if (this.keyExists(taxonomyId, fieldId, key)) {
      return Object
        .keys(this.cache)
        .indexOf(key);
    }

    return -1;
  }

  getRange(taxonomyId: number, fieldId: number, items: string[]): Range {
    const cached = this.taxonomyFieldsCache[taxonomyId];
    const cachedField = this.appHelper.find(cached, f => f.fieldId === fieldId);

    if (cachedField) {
      for (const key of cachedField.keys) {
        const cachedItems = this.cache[key].items;

        if (cachedItems.length === items.length && cachedItems.every(i => this.appHelper.includes(items, i))) {
          return new Range(
            this.range.rowIndex + this.getRowPosition(taxonomyId, fieldId, key) + 1, // header
            this.range.columnIndex + this.permanentColumnsCount,
            1,
            items.length,
            this.range.sheetName);
        }
      }
    }

    return undefined;
  }

  getCascading(rowIndex: number): Cascading {
    const keyIndex = rowIndex - this.range.rowIndex - 1;
    const cacheKeys = Object.keys(this.cache);

    return keyIndex < cacheKeys.length
      ? this.cache[cacheKeys[keyIndex]]
      : undefined;
  }

  remove(taxonomyId: number, fieldId: number, key: string): boolean {
    if (this.keyExists(taxonomyId, fieldId, key)) {
      this.removeTaxonomyFieldKey(taxonomyId, fieldId, key);
      delete this.cache[key];

      if (this.range.rowCount == 2 /* header + one row */) {
        this.containsData = false;
      } else {
        this.range.rowCount--;
      }

      return true;
    }

    return false;
  }

  private cacheTaxonomyField(taxonomyId: number, fieldId: number): string {
    const cache = this.taxonomyFieldsCache[taxonomyId];
    const cachedField = this.appHelper.find(cache, f => f.fieldId === fieldId);
    const key = this.appHelper.getNewGuid();

    if (!cache) {
      this.taxonomyFieldsCache[taxonomyId] = [{ fieldId: fieldId, keys: [key] }];
    } else if (!cachedField) {
      cache.push({ fieldId: fieldId, keys: [key] });
    } else {
      cachedField.keys.push(key);
    }

    return key;
  }

  private removeTaxonomyFieldKey(taxonomyId: number, fieldId: number, key: string): void {
    const cache = this.taxonomyFieldsCache[taxonomyId];
    const cachedFieldIndex = this.appHelper.findIndex(cache, f => f.fieldId === fieldId);

    if (cachedFieldIndex >= 0) {
      const keys = cache[cachedFieldIndex].keys;

      keys.splice(keys.indexOf(key), 1);

      if (keys.length === 0) {
        cache.splice(cachedFieldIndex, 1);

        if (cache.length === 0) {
          delete this.taxonomyFieldsCache[taxonomyId];
        }
      }
    }
  }
};